import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Printer, Eye, RefreshCw } from "lucide-react";
import React from "react";
import { useToast } from "@/components/ui/use-toast";
import { useErpTransactionMutation, useLazyGetTransactionByIdQuery } from "@/services/transactionApi";
import { ReceiptExport } from "@/components/admin/pos/sales_history/table/receipt-component";
import { Transaction } from "@/types/pos/transaction";
import { BudgetLog } from "@/types/budget";
import { ErrorType } from "@/types";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

interface DataTableRowActionsProps {
  log: BudgetLog;
  transactionId: number;
  onViewDetails: (log: BudgetLog) => void;
  onRetry: (log: BudgetLog) => void;
}

export function DataTableRowActions({
  log,
  transactionId,
  onViewDetails,
  onRetry,
}: DataTableRowActionsProps) {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { toast } = useToast();
  const [isLoadingPrint, setIsLoadingPrint] = React.useState(false);

  // Use lazy query for transaction data - only call when needed
  const [getTransactionById, { data: transactionData, isLoading: isLoadingTransaction }] = useLazyGetTransactionByIdQuery();

  async function buildPrintHtml(transactionData?: Transaction): Promise<string> {
    if (transactionData) {
      return await ReceiptExport(transactionData);
    }
    return '';
  }

  function printHtml(htmlContent: string) {
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0px";
    iframe.style.height = "0px";
    iframe.style.border = "none";
    iframe.style.visibility = "hidden";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow?.document || iframe.contentDocument;
    if (!iframeDoc) return;
    iframeDoc.open();
    iframeDoc.write(htmlContent || "");
    iframeDoc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 100);
    };
  }

  const handlePrint = async () => {
    setIsLoadingPrint(true);
    try {
      // Use the transaction_id passed from the table
      console.log("Fetching transaction data for ID:", transactionId);

      // Get transaction data using the transaction_id from ERP log
      const transactionResult = await getTransactionById({ transaction_id: transactionId, org_id: orgId });

      if (transactionResult.error) {
        console.error("Transaction fetch error:", transactionResult.error);
        toast({
          variant: "destructive",
          title: "Error Loading Transaction",
          description: "Failed to load transaction data. Please try again.",
        });
        return;
      }

      if (!transactionResult.data) {
        console.error("No transaction data received");
        toast({
          variant: "destructive",
          title: "Error Loading Transaction",
          description: "Transaction data not available. Please try again.",
        });
        return;
      }

      console.log("Transaction data received:", {
        id: transactionResult.data.id,
        total: transactionResult.data.total,
        items: transactionResult.data.items?.length || 0
      });

      // Generate and print the HTML
      const html = await buildPrintHtml(transactionResult.data);
      printHtml(html);

    } catch (error) {
      console.error("Print error:", error);
      toast({
        variant: "destructive",
        title: "Print Error",
        description: "Failed to generate print content. Please try again.",
      });
    } finally {
      setIsLoadingPrint(false);
    }
  };

  const [erpTransaction, {isSuccess}] = useErpTransactionMutation()
  const handleErpTransaction = async () => {
    try {
      const resp = await erpTransaction(transactionId).unwrap();
      console.log("resp", { resp });
      if (resp.status === "Success") {
        toast({
          variant: "success",
          title: "ERP Transaction Successful",
        });
      } else {
        toast({
          variant: "destructive",
          title: "ERP Transaction Failed",
        });
      }
    } catch (error) {
      console.error("Error here:", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          >
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-full">
          <DropdownMenuItem onClick={() => onViewDetails(log)}>
            <Eye className="mr-2 h-4 w-4" />
            View Details
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={handlePrint}
            disabled={isLoadingPrint || isLoadingTransaction}
          >
            {isLoadingPrint ? (
              <i className="fa fa-spinner fa-spin mr-2 h-4 w-4"></i>
            ) : (
              <Printer className="mr-2 h-4 w-4" />
            )}
            {isLoadingPrint ? "Printing..." : "Print Invoice"}
          </DropdownMenuItem>
          {log.status.toLowerCase() === 'failed' && (
            <DropdownMenuItem
              onClick={handleErpTransaction}
              className="text-nowrap"
            >
              <i className="fa-solid fa-arrow-rotate-right mr-2"></i>
              Retry ERP
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
